/**
 * Activity ranges + the range rollup — the ONE definition of "what does '7 days'
 * mean" and "what did the platform do over those windows".
 *
 * The dashboard's range segments, the client-side tile aggregation, and the
 * server-side Activity Summary generator all read {@link ACTIVITY_RANGES}: a
 * range must never mean 42 windows in the browser and 40 in the callable.
 *
 * {@link rollupActivity} is pure and server-side-only in practice (the callable
 * feeds its output to the summarizer), but it lives here rather than in the
 * functions service because it is defined BY the range table above it.
 */
import type {
  ActivityRangeKey,
  AdminActorRow,
  AdminGameActionRow,
  AdminGameEngagementRow,
  AdminNewGameRow,
  AdminPlatformWindow,
  AdminStatsSnapshot,
} from './types.js';

/** How many 4h windows each range covers. `null` = every retained window. */
export const ACTIVITY_RANGES: ReadonlyArray<{
  key: ActivityRangeKey;
  label: string;
  windows: number | null;
}> = [
  { key: '4h', label: '4 hours', windows: 1 },
  { key: '24h', label: '24 hours', windows: 6 },
  { key: '3d', label: '3 days', windows: 18 },
  { key: '7d', label: '7 days', windows: 42 },
  { key: '14d', label: '14 days', windows: 84 },
  { key: '30d', label: '30 days', windows: 180 },
  { key: 'all', label: 'All time', windows: null },
];

const RANGE_KEYS = new Set<string>(ACTIVITY_RANGES.map((range) => range.key));

/**
 * Caps on what the rollup hands the summarizer. An 'all' range merges every
 * retained window, so without these the prompt grows without bound as the
 * platform does — and an oversized prompt is how this codebase has been bitten
 * before (latency first, then truncation). The agent needs the most notable
 * rows, not every row; what is dropped is counted in `truncated` so the summary
 * can say so rather than quietly implying it saw everything.
 */
export const ROLLUP_CAPS = {
  topGames: 25,
  newGames: 25,
  actionsByGame: 40,
  actionsByActor: 15,
  actionTotals: 25,
  /** Candidate merges the agent picks the notable few from. */
  shipped: 15,
} as const;

/**
 * Conventional-commit prefixes worth celebrating. `feat` is the signal; `fix`
 * and `perf` are included because a user-visible fix IS an improvement, and the
 * agent is asked to judge which ones qualify. Everything else (chore, docs,
 * test, refactor, ops, build, ci) is real work but not news for this panel.
 */
const SHIPPED_PREFIX = /^(feat|fix|perf)(\([^)]*\))?!?:/i;

/** Narrow untrusted callable input to a real range key. */
export function isActivityRangeKey(value: unknown): value is ActivityRangeKey {
  return typeof value === 'string' && RANGE_KEYS.has(value);
}

/** Window count for a range (`null` = all retained windows). */
export function windowsForRange(key: ActivityRangeKey): number | null {
  return ACTIVITY_RANGES.find((range) => range.key === key)?.windows ?? null;
}

/** A game's engagement DELTA across the range (diffed cumulative counters). */
export interface ActivityGameDelta {
  slug: string;
  title: string;
  kind: 'creation' | 'native';
  creatorHandle: string;
  plays: number;
  views: number;
  likes: number;
  comments: number;
  /**
   * True when the game had no baseline sample before the range, so its numbers
   * are all-time-to-date rather than a true in-range delta.
   */
  firstSeenInRange: boolean;
}

/** Everything the summarizer is allowed to know about a range. */
export interface ActivityRollup {
  rangeKey: ActivityRangeKey;
  coveredFrom: number;
  coveredTo: number;
  windowCount: number;
  /** Windows carrying named detail. Below `windowCount` = partial naming. */
  detailWindows: number;
  /** Summed {@link AdminPlatformWindow} counters across the range. */
  totals: AdminPlatformWindow;
  /** Platform-wide cumulative deltas (plays/views/sessions) across the range. */
  platformDeltas: {
    totalPlays: number | null;
    gameViews: number | null;
    pageViews: number | null;
    assetsAttached: number | null;
  };
  /**
   * Merges into main during the range that plausibly changed the product.
   *
   * A merge is the honest evidence available: this repo deploys from main by
   * hand, and only the builder/game-server leave Cloud Run revisions behind
   * (Firebase Hosting leaves none), so "shipped" cannot be proven per change.
   * `deployRevisions` is reported alongside so the agent can see whether any
   * service deploy happened at all in the window.
   */
  shipped: ActivityShippedCandidate[];
  deployRevisions: number;
  topGames: ActivityGameDelta[];
  newGames: AdminNewGameRow[];
  actionsByGame: AdminGameActionRow[];
  actionsByActor: AdminActorRow[];
  /** Action totals across the range, for grounding claims about admin/creator work. */
  actionTotals: Array<{ action: string; count: number }>;
  /** Which lists were cut to {@link ROLLUP_CAPS}, so the agent can say so. */
  truncated: {
    topGames: number;
    newGames: number;
    actionsByGame: number;
    shipped: number;
  };
}

/** One merge the agent may turn into a celebrated, explained line. */
export interface ActivityShippedCandidate {
  pr: number;
  title: string;
  author: string;
  mergedAt: number;
  /** `feat` | `fix` | `perf`, parsed from the conventional-commit subject. */
  kind: string;
}

const EMPTY_TOTALS = (): AdminPlatformWindow => ({
  gamesCreated: 0,
  nativeGamesAdded: 0,
  v1GamesLive: 0,
  completedForks: 0,
  gameIterations: 0,
  assetVariantsGenerated: 0,
  multiplayerMatches: 0,
  newUsers: 0,
  newSessions: 0,
  likes: 0,
  comments: 0,
});

/** Cumulative counters only ever go up; a reset or a gap yields null, not noise. */
function counterDelta(current: number | undefined, baseline: number | undefined): number | null {
  if (current == null || baseline == null || current < baseline) return null;
  return current - baseline;
}

/**
 * Roll `snapshots` (newest first, as the repo returns them) up into everything
 * the Activity Summary agent sees.
 *
 * `baseline` is the snapshot immediately BEFORE the range — needed because
 * per-game plays/views are cumulative counters with no event ledger behind them,
 * so an in-range delta is `latest − before-the-range`. Without it a 1-window
 * range could report no engagement at all.
 */
export function rollupActivity(
  snapshots: ReadonlyArray<AdminStatsSnapshot>,
  baseline: AdminStatsSnapshot | null,
  rangeKey: ActivityRangeKey,
): ActivityRollup | null {
  const latest = snapshots[0];
  const oldest = snapshots[snapshots.length - 1];
  if (!latest || !oldest) return null;

  const totals = EMPTY_TOTALS();
  const actionTotals = new Map<string, number>();
  const actionsByGame = new Map<string, AdminGameActionRow>();
  const actionsByActor = new Map<string, number>();
  const newGames: AdminNewGameRow[] = [];
  const shipped = new Map<number, ActivityShippedCandidate>();
  let deployRevisions = 0;
  let detailWindows = 0;

  for (const snapshot of snapshots) {
    // Shipping evidence: merges into main, deduped by PR number (a retried
    // window re-reports the same merges), plus any service deploy that happened.
    for (const pr of snapshot.quant.github?.mergedPrs ?? []) {
      const match = SHIPPED_PREFIX.exec(pr.title);
      if (!match) continue;
      shipped.set(pr.number, {
        pr: pr.number,
        title: pr.title,
        author: pr.author,
        mergedAt: pr.mergedAt,
        kind: match[1].toLowerCase(),
      });
    }
    deployRevisions += snapshot.quant.gcp?.deploys?.length ?? 0;

    const pg = snapshot.quant.postgres;
    const window = pg?.platformWindow;
    if (window) {
      for (const key of Object.keys(totals) as Array<keyof AdminPlatformWindow>) {
        totals[key] += window[key] ?? 0;
      }
    }
    for (const row of pg?.actionLogWindow ?? []) {
      actionTotals.set(row.action, (actionTotals.get(row.action) ?? 0) + row.count);
    }
    const detail = pg?.activityDetail;
    if (!detail) continue;
    detailWindows += 1;
    for (const row of detail.actionsByGame) {
      const key = `${row.slug}\u0000${row.action}`;
      const current = actionsByGame.get(key);
      if (current) current.count += row.count;
      else actionsByGame.set(key, { ...row });
    }
    for (const row of detail.actionsByActor) {
      actionsByActor.set(row.handle, (actionsByActor.get(row.handle) ?? 0) + row.actions);
    }
    newGames.push(...detail.newGames);
  }

  // Per-game engagement: diff the newest sample against the pre-range baseline
  // (falling back to the oldest IN-range sample when there is no earlier window,
  // e.g. the very first snapshots ever collected).
  const baseSample = baseline ?? (snapshots.length > 1 ? oldest : null);
  const baseRows = new Map<string, AdminGameEngagementRow>(
    (baseSample?.quant.postgres?.activityDetail?.gameEngagement ?? []).map((row) => [
      row.creationId,
      row,
    ]),
  );
  const topGames: ActivityGameDelta[] = [];
  for (const row of latest.quant.postgres?.activityDetail?.gameEngagement ?? []) {
    const before = baseRows.get(row.creationId);
    const delta = {
      plays: counterDelta(row.plays, before?.plays ?? 0) ?? 0,
      views: counterDelta(row.views, before?.views ?? 0) ?? 0,
      likes: counterDelta(row.likes, before?.likes ?? 0) ?? 0,
      comments: counterDelta(row.comments, before?.comments ?? 0) ?? 0,
    };
    if (delta.plays + delta.views + delta.likes + delta.comments === 0) continue;
    topGames.push({
      slug: row.slug,
      title: row.title,
      kind: row.kind,
      creatorHandle: row.creatorHandle,
      ...delta,
      firstSeenInRange: before == null,
    });
  }
  topGames.sort((a, b) => b.plays - a.plays || b.views - a.views);

  const sortedNewGames = newGames.sort((a, b) => b.createdAt - a.createdAt);
  // Features first, then newest — the agent reads top-down and the cap should
  // never drop a `feat` in favour of a routine fix.
  const sortedShipped = [...shipped.values()].sort(
    (a, b) =>
      Number(b.kind === 'feat') - Number(a.kind === 'feat') || b.mergedAt - a.mergedAt,
  );
  const sortedActionsByGame = [...actionsByGame.values()].sort((a, b) => b.count - a.count);

  const latestTotals = latest.quant.postgres?.platformTotals;
  const baseTotals = baseSample?.quant.postgres?.platformTotals;

  return {
    rangeKey,
    coveredFrom: oldest.windowStart,
    coveredTo: latest.windowEnd,
    windowCount: snapshots.length,
    detailWindows,
    totals,
    platformDeltas: {
      totalPlays: counterDelta(latestTotals?.totalPlays, baseTotals?.totalPlays),
      gameViews: counterDelta(latestTotals?.gameViews, baseTotals?.gameViews),
      pageViews: counterDelta(latestTotals?.pageViews, baseTotals?.pageViews),
      assetsAttached: counterDelta(latestTotals?.assetsAttached, baseTotals?.assetsAttached),
    },
    shipped: sortedShipped.slice(0, ROLLUP_CAPS.shipped),
    deployRevisions,
    topGames: topGames.slice(0, ROLLUP_CAPS.topGames),
    newGames: sortedNewGames.slice(0, ROLLUP_CAPS.newGames),
    actionsByGame: sortedActionsByGame.slice(0, ROLLUP_CAPS.actionsByGame),
    actionsByActor: [...actionsByActor.entries()]
      .map(([handle, actions]) => ({ handle, actions }))
      .sort((a, b) => b.actions - a.actions)
      .slice(0, ROLLUP_CAPS.actionsByActor),
    actionTotals: [...actionTotals.entries()]
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, ROLLUP_CAPS.actionTotals),
    truncated: {
      topGames: Math.max(0, topGames.length - ROLLUP_CAPS.topGames),
      newGames: Math.max(0, sortedNewGames.length - ROLLUP_CAPS.newGames),
      actionsByGame: Math.max(0, sortedActionsByGame.length - ROLLUP_CAPS.actionsByGame),
      shipped: Math.max(0, sortedShipped.length - ROLLUP_CAPS.shipped),
    },
  };
}
